import { createContext, useContext, useEffect, useState, useRef } from "react";
import { auth, identifyFirebaseUser, logEvent } from "@/lib/firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User,
} from "firebase/auth";
import { initClarity, identifyUser } from "@/lib/clarity";
import type { UserRole, Membership } from "@/lib/api/types";

const EMAIL_LINK_KEY = "emailForSignIn";

function friendlyAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  const message = (err as { message?: string })?.message ?? "";
  switch (code) {
    case "auth/invalid-action-code": return "This sign-in link has expired or was already used. Please request a new one.";
    case "auth/expired-action-code": return "This sign-in link has expired. Please request a new one.";
    case "auth/user-disabled": return "This account has been disabled. Contact your program manager.";
    case "auth/user-not-found": return "No account found for this email. Contact your program manager.";
    case "auth/invalid-email": return "Please enter a valid email address.";
    case "auth/too-many-requests": return "Too many attempts. Please wait a few minutes and try again.";
    case "auth/network-request-failed": return "Network error. Check your internet connection and try again.";
    case "auth/popup-closed-by-user": return "";
    case "auth/popup-blocked": return "Popup was blocked by your browser. Allow popups for this site or try the email sign-in option.";
    case "auth/account-exists-with-different-credential": return "An account already exists with this email using a different sign-in method.";
    case "auth/unauthorized-continue-uri": return "Sign-in configuration error. Contact your program manager.";
    case "auth/operation-not-allowed": return "This sign-in method is not enabled. Contact your program manager.";
    default:
      if (message.includes("not registered") || message.includes("PERMISSION_DENIED"))
        return "You are not registered in the program. Contact your program manager.";
      return message || "Sign-in failed. Please try again.";
  }
}

interface AuthCtx {
  user: User | null;
  role: UserRole;
  memberships: Membership[];
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  sendEmailLink: (email: string) => Promise<void>;
  completeEmailLink: (email: string) => Promise<void>;
  pendingEmailLink: boolean;
  emailLinkError: string;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx>({
  user: null, 
  role: "unknown", 
  memberships: [], 
  loading: true, 
  pendingEmailLink: false, 
  emailLinkError: "",
  signInWithGoogle: async () => { await Promise.resolve(); }, 
  sendEmailLink: async (_email: string) => { await Promise.resolve(); }, 
  completeEmailLink: async (_email: string) => { await Promise.resolve(); }, 
  logout: async () => { await Promise.resolve(); },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>("unknown");
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingEmailLink, setPendingEmailLink] = useState(false);
  const [emailLinkError, setEmailLinkError] = useState("");
  const isNewSignIn = useRef(false);

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const storedEmail = window.localStorage.getItem(EMAIL_LINK_KEY);
      if (storedEmail) {
        signInWithEmailLink(auth, storedEmail, window.location.href)
          .then(() => {
            window.localStorage.removeItem(EMAIL_LINK_KEY);
            isNewSignIn.current = true;
            window.history.replaceState(null, "", window.location.pathname);
          })
          .catch((err) => {
            window.history.replaceState(null, "", window.location.pathname);
            setEmailLinkError(friendlyAuthError(err));
          });
      } else {
        // Different device — show email input on Login page
        setPendingEmailLink(true);
      }
    }
  }, []);

  useEffect(() => {
    initClarity();
    return onAuthStateChanged(auth, (u) => {
      const handleAuthChange = async () => {
        setUser(u);
        if (u) {
          try {
            // Use cached token first for fast load; force refresh only if claims are missing
            let result = await u.getIdTokenResult(false);
            if (!result.claims.role) {
              result = await u.getIdTokenResult(true);
            }
            setRole((result.claims.role as UserRole) || "unknown");
            setMemberships((result.claims.memberships as Membership[]) || []);
          } catch {
            setRole("unknown");
            setMemberships([]);
          }
          identifyUser(u.uid, u.email ?? undefined, u.displayName ?? undefined);
          identifyFirebaseUser(u.uid);
          // Only log on actual sign-in, not page reload
          if (isNewSignIn.current) {
            logEvent("login", { method: "google", app_name: window.location.hostname });
            isNewSignIn.current = false;
          }
        } else {
          setRole("unknown");
          setMemberships([]);
        }
        setLoading(false);
      };
      void handleAuthChange();
    });
  }, []);

  const signInWithGoogle = async () => {
    isNewSignIn.current = true;
    await signInWithPopup(auth, new GoogleAuthProvider());
  };

  const sendEmailLink = async (email: string) => {
    await sendSignInLinkToEmail(auth, email, {
      url: window.location.origin + "/login",
      handleCodeInApp: true,
    });
    window.localStorage.setItem(EMAIL_LINK_KEY, email);
  };

  const completeEmailLink = async (email: string) => {
    isNewSignIn.current = true;
    await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem(EMAIL_LINK_KEY);
    setPendingEmailLink(false);
    window.history.replaceState(null, "", window.location.pathname);
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, role, memberships, loading, pendingEmailLink, emailLinkError, signInWithGoogle, sendEmailLink, completeEmailLink, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
